import { jsPDF } from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";
export function exportToCSV({
  data,
  filename = "export.csv",
  columns, // [{ key: 'name', header: 'Name' }, ...] optional; falls back to keys from data
  delimiter = ",",
  addBOM = true, // Excel-friendly UTF-8 BOM
  newline = "CRLF", // 'CRLF' | 'LF'
  valueFormatter, // (value, row, key) => string
} = {}) {
  if (!Array.isArray(data) || data.length === 0) {
    console.warn("exportToCSV: no data");
    return;
  }

  // Build column schema
  let cols =
    columns && columns.length
      ? columns
      : Array.from(
          data.reduce((set, row) => {
            Object.keys(row || {}).forEach((k) => set.add(k));
            return set;
          }, new Set())
        ).map((key) => ({ key, header: key }));

  const eol = newline === "CRLF" ? "\r\n" : "\n";

  // RFC 4180: escape quotes by doubling, quote fields with commas, quotes, or newlines
  const escapeCell = (raw) => {
    let v = raw;
    if (v === null || v === undefined) v = "";
    if (typeof v === "object") v = JSON.stringify(v);
    v = String(v);
    const hasSpecial =
      v.includes('"') ||
      v.includes(delimiter) ||
      v.includes("\n") ||
      v.includes("\r");
    if (v.includes('"')) v = v.replace(/"/g, '""');
    return hasSpecial ? `"${v}"` : v;
  };

  const formatCell = (value, row, key) =>
    escapeCell(valueFormatter ? valueFormatter(value, row, key) : value);

  const headerLine = cols
    .map((c) => escapeCell(c.header ?? c.key))
    .join(delimiter);
  const bodyLines = data.map((row) =>
    cols.map((c) => formatCell(row?.[c.key], row, c.key)).join(delimiter)
  );

  let csv = [headerLine, ...bodyLines].join(eol);
  if (addBOM) {
    // Prepend UTF-8 BOM for Excel compatibility
    csv = "\uFEFF" + csv;
  }

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportToPDF({
  data,
  filename = "export.pdf",
  columns,
  title = "Report",
  orientation = "p",
  unit = "mm",
  format = "a4",
  theme = "striped",
  cellFormatter,
  styles = { fontSize: 10 },
  headStyles = { fillColor: [22, 160, 133], halign: "center" },
} = {}) {
  if (!Array.isArray(data) || data.length === 0) return;

  const cols =
    columns && columns.length
      ? columns
      : Array.from(
          data.reduce((set, r) => {
            Object.keys(r || {}).forEach((k) => set.add(k));
            return set;
          }, new Set())
        ).map((key) => ({ key, header: key }));

  const headers = cols.map((c) => c.header ?? c.key);
  const rows = data.map((row) =>
    cols.map((c) => {
      const v = cellFormatter
        ? cellFormatter(row?.[c.key], row, c.key)
        : row?.[c.key] ?? "";
      return typeof v === "object" ? JSON.stringify(v) : String(v);
    })
  );

  const doc = new jsPDF(orientation, unit, format);

  doc.setFontSize(14);
  doc.text(title, 10, 10);
  doc.setFontSize(styles.fontSize ?? 10);

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 16,
    theme,
    styles,
    headStyles,
    didDrawPage: (dataCtx) => {
      const total = doc.internal.getNumberOfPages();
      const pageWidth = doc.internal.pageSize.getWidth
        ? doc.internal.pageSize.getWidth()
        : doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.getHeight
        ? doc.internal.pageSize.getHeight()
        : doc.internal.pageSize.height;
      doc.setFontSize(10);
      doc.text(
        `Page ${dataCtx.pageNumber} of ${total}`,
        pageWidth / 2,
        pageHeight - 8,
        { align: "center" }
      );
    },
  });

  doc.save(filename);
}
